import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import './LanguageSwitcher.css';

// Mobil uygulamadaki LanguageSelector ile aynı sıra/ad/bayrak
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ur', name: 'اردو', flag: '🇵🇰' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'sr', name: 'Српски', flag: '🇷🇸' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'mk', name: 'Македонски', flag: '🇲🇰' },
  { code: 'hu', name: 'Magyar', flag: '🇭🇺' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'fa', name: 'فارسی', flag: '🇮🇷' },
  { code: 'bn', name: 'বাংলা', flag: '🇧🇩' },
];

// Dil seçimi: mobildeki gibi alttan açılan liste (bottom sheet); masaüstünde
// ortada panel. Header'lardaki backdrop-filter fixed konumu bozmasın diye
// body'ye portal ile render edilir.
const LanguageSwitcher = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const activeRef = useRef(null);

  const currentCode = (i18n.language || 'tr').split('-')[0];
  const current = LANGUAGES.find((lang) => lang.code === currentCode);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Seçili dili görünür alana kaydır
    activeRef.current?.scrollIntoView({ block: 'center' });
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  return (
    <div className="language-switcher">
      <button
        type="button"
        className="language-button"
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-label={t('language.title')}
      >
        <span className="current-flag">{current?.flag || '🌐'}</span>
        <span className="current-language">{current?.name || 'Language'}</span>
        <span className="dropdown-arrow">▼</span>
      </button>

      {isOpen && createPortal(
        <div className="lang-sheet-backdrop" onClick={() => setIsOpen(false)}>
          <div
            className="lang-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lang-sheet-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lang-sheet-handle" />
            <div className="lang-sheet-header">
              <span className="lang-sheet-icon" aria-hidden="true">🌐</span>
              <h2 id="lang-sheet-title" className="lang-sheet-title">{t('language.title')}</h2>
              <button
                type="button"
                className="lang-sheet-close"
                onClick={() => setIsOpen(false)}
                aria-label={t('authprompt.close')}
              >
                ✕
              </button>
            </div>
            <ul className="lang-sheet-list">
              {LANGUAGES.map((language) => {
                const selected = language.code === currentCode;
                return (
                  <li key={language.code}>
                    <button
                      type="button"
                      ref={selected ? activeRef : null}
                      className={`lang-item ${selected ? 'selected' : ''}`}
                      onClick={() => changeLanguage(language.code)}
                      aria-pressed={selected}
                      lang={language.code}
                    >
                      <span className="lang-item-flag">{language.flag}</span>
                      <span className="lang-item-name">{language.name}</span>
                      {selected && <span className="lang-item-check" aria-hidden="true">✓</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default LanguageSwitcher;
